/**
 * useRoleDetails — TanStack Query composable
 *
 * Single source of truth for role details data.
 * Replaces the Vuex `user` module's `roleDetails` state.
 *
 * The ['role-details'] query is pre-populated at startup in
 * src/stores/auth.ts (checkToken). This composable reads from that cache.
 */
import { computed } from 'vue'
import { useQuery } from '@tanstack/vue-query'
import apiInterceptor from '@/shared/apiInterceptor'
import { environment } from '@/shared/env'
import type { RoleDetails } from '@/shared/types'

export const roleDetailsQueryOptions = {
  queryKey: ['role-details'] as const,
  queryFn: async (): Promise<RoleDetails> => {
    const r = await apiInterceptor.get(environment('VUE_APP_API_URL') + 'roles/v1/role-details/')
    return r ?? {}
  },
  staleTime: Infinity,
}

export function useRoleDetails() {
  const { data, isLoading, isError, error } = useQuery(roleDetailsQueryOptions)
  const roleDetails = computed<RoleDetails>(() => data.value ?? {})

  return {
    roleDetails,
    isLoading,
    isError,
    error,
  }
}
